import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  CContainer, 
  CRow, 
  CCol, 
  CCard, 
  CCardHeader,
  CCardBody, 
  CButton, 
  CBadge, 
  CSpinner 
} from '@coreui/react';
import api from '../../api/axiosInstance';
import AnnouncementBanner from '../../components/AnnouncementBanner'; // Import AnnouncementBanner

// Helper 1: HTML se plain text nikalna (Max 50 Chars)
const getTruncatedText = (htmlContent, maxLength = 50) => {
  if (!htmlContent) return '';
  const doc = new DOMParser().parseFromString(htmlContent, 'text/html');
  const plainText = doc.body.textContent || "";
  return plainText.trim().substring(0, maxLength);
};

// Helper 2: Clean Slug Generator
const createCleanSlug = (rawUrl) => {
  if (!rawUrl || typeof rawUrl !== 'string') return '';

  return rawUrl
    .replace(/<[^>]*>/g, '')
    .replace(/%C2%A0/g, '-')
    .replace(/%20/g, '-')
    .replace(/[\/\\]/g, '-')
    .replace(/[^a-zA-Z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
};

const QuestionBank = () => {
  const { section, title } = useParams();
  const navigate = useNavigate();

  const [courseData, setCourseData] = useState(null);
  const [articleData, setArticleData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [qYear, setQYear] = useState(null);

  // Section Check Flags
  const isBeetaSection = section?.toLowerCase() === 'beeta';
  const isDeltaSection = section?.toLowerCase() === 'delta';

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalQuestions, setTotalQuestions] = useState(0);
  
  const itemsPerPage = 9; 

  const defaultThumbnail = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='350' height='200' viewBox='0 0 350 200'%3E%3Crect width='100%25' height='100%25' fill='%23f1f5f9'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='sans-serif' font-size='16px' fill='%2394a3b8'%3ENo Image Available%3C/text%3E%3C/svg%3E";

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [section, title]);

  useEffect(() => {
    const fetchCourseContent = async () => {
      try {
        setLoading(true);
        setError(null);

        const formattedTitle = title;
        let articleFound = false;

        // 1. Article aur Years API Calls Parallel execute karein
        const [articleRes, yearRes] = await Promise.allSettled([
          api.get(`/api/content/GetArticleDetailsBySectionandTitle/${section}/${formattedTitle}`, { skipAuthCheck: true }),
          api.get(`/api/content/GetYearsByCategory/${formattedTitle}`)
        ]);

        // Handle Article Response
        if (articleRes.status === 'fulfilled' && articleRes.value.data) {
          setArticleData(articleRes.value.data);
          articleFound = true;
        } else {
          setArticleData(null);
        }

        // Handle Years Response safely
        if (yearRes.status === 'fulfilled' && yearRes.value.data) {
          setQYear(yearRes.value.data);
        } else {
          setQYear(null);
        }

        // 2. Fetch Course Content IF non-beeta OR article not found
        if (!isBeetaSection || !articleFound) {
          try {
            const courseRes = await api.get(
              `/api/Admin/GetCourseContent?section=${section || 'Alpha'}&title=${formattedTitle || 'SchoolLevel'}&page=${currentPage}&pageSize=${itemsPerPage}`,
              { skipAuthCheck: true }
            );

            setCourseData(courseRes.data);

            if (courseRes.data) {
              const totalCount = courseRes.data.totalQuestions || (courseRes.data.questions ? courseRes.data.questions.length : 0);
              const calculatedPages = courseRes.data.totalPages || Math.ceil(totalCount / itemsPerPage) || 1;
              setTotalQuestions(totalCount);
              setTotalPages(calculatedPages);
            }
          } catch (courseErr) {
            console.error("Failed to load course content:", courseErr);
            setCourseData(null);
          }
        }

      } catch (err) {
        console.error("Error fetching content data:", err);
        setError("Failed to load details.");
      } finally {
        setLoading(false);
      }
    };

    fetchCourseContent();
  }, [section, title, currentPage, isBeetaSection]);

  useEffect(() => {
    setCurrentPage(1);
  }, [section, title]);

  const getImageUrl = (thumbUrl) => {
    if (!thumbUrl) return defaultThumbnail;
    return thumbUrl;
  };

  const handleGetSolution = (q) => {
    const rawQuestionText = q.customUrl || q.customeUrl || q.questionText || q.QuestionText || '';
    const truncatedText = getTruncatedText(rawQuestionText, 50);
    const slug = createCleanSlug(truncatedText);

    navigate(`/content/view-question/${q.id}/${slug}`);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <div className="text-center mt-5 py-5">
        <CSpinner color="primary" size="lg" />
        <p className="text-muted mt-2">Loading Content...</p>
      </div>
    );
  }

  if (error || (!isBeetaSection && !courseData && !articleData)) {
    return (
      <CContainer className="text-center mt-5 py-4">
        <h5 className="text-danger mb-3">{error || "Data loading failed"}</h5>
        <CButton color="primary" size="sm" onClick={() => navigate('/')}>Back to Home</CButton>
      </CContainer>
    );
  }

  const currentQuestions = courseData?.questions || [];

  return (
    <div style={{ overflowX: 'hidden', width: '100%' }}>
      {/* 🌟 1. BANNER FOR BEETA / ARTICLE SECTION */}
      {articleData && (
        <div 
          className="text-white text-center py-5 shadow-sm mb-4" 
          style={{ 
            backgroundColor: 'rgb(0, 33, 71)',
            width: '100vw',
            position: 'relative',
            left: '50%',
            transform: 'translateX(-50%)'
          }}
        >
          <div className="container py-2 px-3">
            <CBadge 
              color="light"
              className="mb-3 px-3 py-2 fs-6 mx-auto rounded-pill shadow-xs text-primary fw-bold"
              style={{ letterSpacing: '0.5px' }}
            >
              {(articleData.section || section).toUpperCase()} - {articleData.cTitle || articleData.title}
            </CBadge>
            <h1 className="fw-bold mb-2  display-6" style={{ color: 'yellow' }}>
              {articleData.heading || articleData.title}
            </h1>
            <AnnouncementBanner position="below-article-title" />
            <p className="lead m-0 opacity-90 text-light fs-6">
              Questions Bank & Solutions
            </p>
          </div>
        </div>
      )}

      {/* 🌟 2. BANNER FOR NON-BEETA / ALPHA REPOSITORY SECTION */}
      {!isBeetaSection && (
        <div 
          className="text-white text-center py-5 shadow-sm mb-4" 
          style={{ 
            backgroundColor: 'rgb(0, 33, 71)',
            width: '100vw',
            position: 'relative',
            left: '50%',
            transform: 'translateX(-50%)'
          }}
        >
          <div className="container py-2 px-3">
            <span className="text-warning fw-bold text-uppercase" style={{ fontSize: '13px', letterSpacing: '1px' }}>
              {(courseData?.section || section || 'School Level').toUpperCase()} QUESTION REPOSITORY
            </span>
            <h1 className="fw-bold m-0   mt-1 display-6" style={{ color: 'yellow' }}>
              {courseData?.title || title?.replace(/-/g, ' ')}
            </h1>
            <p className="text-light opacity-90 m-0 small mt-2">
              {courseData?.description || `Welcome to the ${courseData?.title || 'School Level'} repository.`}
            </p>
            <div className="mt-3">
              <CBadge color="light" className="text-primary px-3 py-2 fs-6 rounded-pill fw-bold">
                Total Questions: {totalQuestions}
              </CBadge>
            </div>
          </div>
        </div>
      )}

      {/* 🚀 MAIN CONTENT CONTAINER */}
      <CContainer className="pb-5">
        {/* ARTICLE / DELTA DETAILS CONTENT */}
        {articleData && (
          <div className="mb-5 p-4 p-md-5 rounded-4 bg-white shadow-sm border text-center">
            {(articleData.content || articleData.description || articleData.articleContent || articleData.notes) && (
              <div 
                id="delta-notes-section"
                className="article-body ck-content text-start mb-5 p-3 p-md-4 rounded-3 border-start border-4 border-success bg-light shadow-xs overflow-hidden"
                style={{ lineHeight: '1.8', fontSize: '1.05rem', color: '#374151', maxWidth: '100%' }}
              >
                <h4 className="fw-bold text-success mb-3 d-flex align-items-center gap-2">
                  <span>📖</span> Chapter Notes & Content
                </h4>
              
               <div 
  style={{ overflowX: 'auto', maxWidth: '100%' }}
  dangerouslySetInnerHTML={{ 
    __html: (articleData.notes?.description || articleData.content || articleData.description || articleData.articleContent || '')
      .replace(/&nbsp;/g, ' ')
      .replace(/\u00a0/g, ' ')
  }} 
/>
              </div>
            )}

            {/* ACTION CARDS */}
            <div className="d-flex flex-column align-items-center gap-3 mt-4 mx-auto" style={{ maxWidth: '680px' }}>
              <div 
                className="w-100 p-3.5 px-4 rounded-3 text-white d-flex align-items-center justify-content-between shadow-sm custom-action-card"
                style={{ 
                  background: 'linear-gradient(135deg, #0f172a, #1e293b)', 
                  borderLeft: '5px solid #3b82f6',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  cursor: 'pointer'
                }}
                onClick={() => navigate(`/${section}/${title.replace(/\s+/g, '-')}/year-wise`)}
              >
                <div className="d-flex align-items-center gap-3 text-start">
                  <span className="fs-4">📅</span>
                  <div>
                    <div className="fw-bold fs-6">{articleData.cTitle || articleData.title} Previous Year Questions</div>
                    <small className="text-light opacity-75">
                      {qYear?.minYear && qYear?.maxYear 
                        ? `Yearwise: From ${qYear.minYear} to ${qYear.maxYear}` 
                        : 'Yearwise Previous Year Solutions'}
                    </small>
                  </div>
                </div>
                <span className="fs-5 card-arrow">➔</span>
              </div>

              <div 
                className="w-100 p-3.5 px-4 rounded-3 text-white d-flex align-items-center justify-content-between shadow-sm custom-action-card"
                style={{ 
                  background: 'linear-gradient(135deg, #991b1b, #dc2626)', 
                  borderLeft: '5px solid #f87171',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  cursor: 'pointer'
                }}
                onClick={() => navigate(`/${section}/${title.replace(/\s+/g, '-')}/chapter-wise`)}
              >
                <div className="d-flex align-items-center gap-3 text-start">
                  <span className="fs-4">📚</span>
                  <div>
                    <div className="fw-bold fs-6">{articleData.cTitle || articleData.title} Previous Year Questions</div>
                    <small className="text-light opacity-75">Chapterwise / Topic wise Breakdown</small>
                  </div>
                </div>
                <span className="fs-5 card-arrow">➔</span>
              </div>
            </div>
          </div>
        )}

        {/* QUESTIONS GRID FOR NON-BEETA (ALPHA) */}
        {!isBeetaSection && (
          <>
            {currentQuestions.length > 0 ? (
              <CRow className="g-4">
                {currentQuestions.map((q, idx) => {
                  const actualQIndex = totalQuestions - ((currentPage - 1) * itemsPerPage + idx);

                  return (
                    <CCol lg={4} md={6} sm={12} key={q.id}>
                      <CCard className="h-100 border-0 shadow-sm rounded-4 overflow-hidden custom-card-hover" style={{ transition: 'all 0.3s ease' }}>
                        <CCardHeader className="bg-light px-3 py-2.5 border-bottom d-flex align-items-center justify-content-between">
                          <span className="fw-bold text-dark" style={{ fontSize: '0.9rem' }}>
                            📌 Question No: {actualQIndex}
                          </span>
                          <CBadge color="info" className="text-dark" style={{ fontSize: '10px' }}>
                            {q.customQuestionId || `Ref #${q.id}`}
                          </CBadge>
                        </CCardHeader>

                        <CCardBody className="p-2 bg-white d-flex align-items-center justify-content-center">
                          <div className="w-100 rounded-3 overflow-hidden border position-relative" style={{ height: '170px', backgroundColor: '#fdfdfd' }}>
                            <img 
                              src={getImageUrl(q.thumbnail)} 
                              alt={q.customQuestionId || `Question ${q.id}`} 
                              className="w-100 h-100 object-fit-cover hover-zoom"
                              style={{ transition: 'transform 0.3s ease' }}
                              onError={(e) => {
                                e.currentTarget.onerror = null; 
                                e.currentTarget.src = defaultThumbnail;
                              }}
                            />
                          </div>
                        </CCardBody>

                        <div className="p-3 pt-0">
                          <CButton 
                            color="danger" 
                            className="w-100 fw-bold text-white rounded-3 py-2 shadow-sm d-flex align-items-center justify-content-center gap-2"
                            style={{ background: 'linear-gradient(135deg, #e63946, #c1121f)', border: 'none' }}
                            onClick={() => handleGetSolution(q)}
                          >
                            <span>👁️ Get Solution</span>
                          </CButton>
                        </div>
                      </CCard>
                    </CCol>
                  );
                })}
              </CRow>
            ) : (
              <div className="alert alert-warning text-center">No questions available in this category.</div>
            )}

            {/* NAVIGATION / PAGINATION */}
            {totalQuestions > 0 && (
              <div className="mt-5 p-3 bg-white rounded-4 shadow-sm border d-flex align-items-center justify-content-between flex-wrap gap-2">
                <div className="fw-bold text-secondary" style={{ fontSize: '0.9rem' }}>
                  Page <span className="text-dark">{currentPage}</span> of {totalPages}
                </div>

                <div className="d-flex align-items-center gap-1 flex-wrap">
                  <button 
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                    className="btn btn-sm btn-outline-secondary rounded-pill px-3 me-2"
                    style={{ cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
                  >
                    &laquo; Prev
                  </button>

                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`btn btn-sm rounded-circle px-3 py-1 fw-bold ${currentPage === page ? 'btn-danger text-white shadow-sm' : 'btn-light text-dark'}`}
                      style={{ cursor: 'pointer', minWidth: '35px' }}
                    >
                      {page}
                    </button>
                  ))}

                  <button 
                    disabled={currentPage === totalPages}
                    onClick={() => handlePageChange(currentPage + 1)}
                    className="btn btn-sm btn-outline-secondary rounded-pill px-3 ms-2"
                    style={{ cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}
                  >
                    Next &raquo;
                  </button>
                </div>
              </div>
            )}
          </>
        )}

   <style>{`
  .custom-card-hover:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 24px rgba(0,0,0,0.1) !important;
  }
  .custom-card-hover:hover .hover-zoom {
    transform: scale(1.04);
  }

  /* Force whole words to wrap without splitting letters */
  #delta-notes-section,
  #delta-notes-section * {
    word-break: keep-all !important;
    overflow-wrap: break-word !important;
    hyphens: none !important;
    -webkit-hyphens: none !important;
    -ms-hyphens: none !important;
  }

  #delta-notes-section {
    overflow-x: auto !important;
  }

  #delta-notes-section table {
    width: 100% !important;
    max-width: 100% !important;
    border-collapse: collapse !important;
  }

  #delta-notes-section img {
    max-width: 100% !important;
    height: auto !important;
  }
`}</style>
      </CContainer>
    </div>
  );
};

export default QuestionBank;